import {type Extension, RangeSetBuilder, StateEffect, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'

import {UploadPlaceholderWidget, type UploadPlaceholderWidgetData} from '../widgets/upload-placeholder'

type UploadPlaceholderSpec = {
  id: string
  from: number
  to: number
  data: UploadPlaceholderWidgetData
}

type UploadPlaceholderUpdate = {
  id: string
  data: Partial<UploadPlaceholderWidgetData>
}

type PlaceholderEntry = {
  from: number
  to: number
  data: UploadPlaceholderWidgetData
}

type PlaceholderFieldState = {
  entries: Map<string, PlaceholderEntry>
  decorations: DecorationSet
}

export const addUploadPlaceholderEffect = StateEffect.define<UploadPlaceholderSpec>()
export const updateUploadPlaceholderEffect = StateEffect.define<UploadPlaceholderUpdate>()
export const removeUploadPlaceholderEffect = StateEffect.define<{id: string}>()

const uploadPlaceholderField = StateField.define<PlaceholderFieldState>({
  create: () => ({entries: new Map(), decorations: Decoration.none}),
  update: (state, tr) => {
    let entries = state.entries
    let changed = false

    if (tr.docChanged && entries.size > 0) {
      const mapped = new Map<string, PlaceholderEntry>()
      for (const [id, entry] of entries) {
        mapped.set(id, {
          ...entry,
          from: tr.changes.mapPos(entry.from, 1),
          to: tr.changes.mapPos(entry.to, -1),
        })
      }
      entries = mapped
      changed = true
    }

    for (const effect of tr.effects) {
      if (effect.is(addUploadPlaceholderEffect)) {
        if (entries === state.entries) entries = new Map(entries)
        entries.set(effect.value.id, {
          from: effect.value.from,
          to: effect.value.to,
          data: effect.value.data,
        })
        changed = true
      } else if (effect.is(updateUploadPlaceholderEffect)) {
        const existing = entries.get(effect.value.id)
        if (existing) {
          if (entries === state.entries) entries = new Map(entries)
          entries.set(effect.value.id, {
            ...existing,
            data: {...existing.data, ...effect.value.data},
          })
          changed = true
        }
      } else if (effect.is(removeUploadPlaceholderEffect)) {
        if (entries.has(effect.value.id)) {
          if (entries === state.entries) entries = new Map(entries)
          entries.delete(effect.value.id)
          changed = true
        }
      }
    }

    if (!changed) return state

    // Rebuild decorations from scratch for all entries
    const builder = new RangeSetBuilder<Decoration>()
    const sortedEntries = Array.from(entries.values()).sort((a, b) => {
      if (a.from !== b.from) return a.from - b.from
      if (a.to !== b.to) return a.to - b.to
      return (a.data.order ?? 0) - (b.data.order ?? 0)
    })

    for (const entry of sortedEntries) {
      builder.add(
        entry.from,
        entry.to,
        Decoration.replace({
          widget: new UploadPlaceholderWidget(entry.data),
          block: true,
        }),
      )
    }
    const decorations = builder.finish()

    return {
      entries,
      decorations,
    }
  },
  provide: field => EditorView.decorations.from(field, value => value.decorations),
})

export const uploadPlaceholderExtension: Extension = [uploadPlaceholderField]
